import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import { Megaphone, Trash, Warning } from "@phosphor-icons/react";

const DURATIONS = [
  { v: "24", label: "1 day" },
  { v: "72", label: "3 days" },
  { v: "168", label: "7 days" },
  { v: "336", label: "14 days" },
  { v: "720", label: "30 days" },
  { v: "0", label: "Until I remove it" },
];
const LEVELS = [
  { v: "info", label: "Info (blue)" },
  { v: "warning", label: "Warning (amber)" },
  { v: "critical", label: "Critical (red)" },
];
const STATUS_STYLE = {
  live: "bg-emerald-50 text-emerald-700 border-emerald-200",
  expired: "bg-muted text-muted-foreground border-border",
  removed: "bg-muted text-muted-foreground border-border",
};
const LEVEL_DOT = { info: "bg-sky-500", warning: "bg-amber-500", critical: "bg-red-500" };

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function AnnouncementsManager() {
  const [rows, setRows] = useState([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [level, setLevel] = useState("info");
  const [duration, setDuration] = useState("72");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/console/announcements")
      .then((r) => setRows(r.data.announcements || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const post = async () => {
    if (!message.trim()) { toast.error("Enter a message."); return; }
    setBusy(true);
    try {
      await api.post("/console/announcements", {
        message: message.trim(), title: title.trim(), level,
        duration_hours: duration === "0" ? null : Number(duration),
      });
      setTitle(""); setMessage(""); setLevel("info"); setDuration("72");
      toast.success("Announcement posted to every customer's home page.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/console/announcements/${id}`);
      toast.success("Announcement removed.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="announcements-manager">
      <div className="border border-border bg-card p-6">
        <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
          <Megaphone size={20} weight="duotone" /> Broadcast to all customers
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Shows on every customer's Compliance Dashboard, just above the Regional Meetings calendar. Use it to
          announce a new version push, maintenance window, or other alert. Set how long it displays, or remove it anytime.
        </p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Title (optional)</Label>
            <Input data-testid="announcement-title-input" value={title} maxLength={120}
              onChange={(e) => setTitle(e.target.value)} placeholder="Scheduled maintenance" className="mt-1 rounded-none" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Level</Label>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger data-testid="announcement-level-select" className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LEVELS.map((l) => <SelectItem key={l.v} value={l.v} data-testid={`announcement-level-${l.v}`}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Message</Label>
          <textarea data-testid="announcement-message-input" value={message} rows={3} maxLength={1000}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="A new version of ClaroShield will be deployed tonight at 10pm CT. Expect ~5 minutes of downtime."
            className="mt-1 w-full border border-border bg-background p-3 text-sm rounded-none focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Display for</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger data-testid="announcement-duration-select" className="mt-1 rounded-none w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DURATIONS.map((d) => <SelectItem key={d.v} value={d.v} data-testid={`announcement-duration-${d.v}`}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="announcement-post-btn" onClick={post} disabled={busy}
            className="rounded-none bg-[#071B3E] hover:bg-[#0E2A5E]">
            <Megaphone size={15} className="mr-1.5" /> {busy ? "Posting…" : "Post announcement"}
          </Button>
        </div>
      </div>

      <div className="border border-border bg-card">
        <div className="border-b border-border px-5 py-3">
          <div className="overline text-muted-foreground">Posted announcements</div>
        </div>
        {rows.length === 0 ? (
          <div className="px-5 py-6 text-sm text-muted-foreground" data-testid="announcements-empty">No announcements posted yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="announcements-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                <th className="px-5 py-2">Message</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Posted</th>
                <th className="px-3 py-2">Expires</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="border-b border-border align-top" data-testid={`announcement-row-${a.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-start gap-2">
                      <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${LEVEL_DOT[a.level] || LEVEL_DOT.info}`} />
                      <div className="min-w-0">
                        {a.title && <div className="font-semibold text-foreground">{a.title}</div>}
                        <div className="text-muted-foreground whitespace-pre-wrap break-words max-w-md">{a.message}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-block text-[11px] font-semibold border px-2 py-0.5 rounded-none ${STATUS_STYLE[a.status] || ""}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground font-mono">{fmt(a.created_at)}</td>
                  <td className="px-3 py-3 text-xs text-muted-foreground font-mono">{a.expires_at ? fmt(a.expires_at) : "No expiry"}</td>
                  <td className="px-3 py-3 text-right">
                    <Button variant="outline" size="sm" data-testid={`announcement-remove-${a.id}`}
                      onClick={() => remove(a.id)} className="rounded-none text-red-600 border-red-200 hover:bg-red-50">
                      <Trash size={14} className="mr-1" /> Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
